import React, { useState, useEffect, useRef } from 'react'
import { IconMeditation, IconHeart } from '../../components/ui/Icons'

export function MeditacionTab({ polar, demo, hrv, isFullscreen, setIsFullscreen, onUploadSession }) {
  const isDarkTheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  const [isActive, setIsActive] = useState(false)
  const [pattern, setPattern] = useState('coherence')
  const [breathingPhase, setBreathingPhase] = useState('inhale')
  const [breathingSeconds, setBreathingSeconds] = useState(5)
  const [stepDuration, setStepDuration] = useState(5)
  const [meditationSeconds, setMeditationSeconds] = useState(0)

  // Stats to compare at the end
  const [startHr, setStartHr] = useState(null)
  const [startRmssd, setStartRmssd] = useState(null)
  const [historyAtStartLength, setHistoryAtStartLength] = useState(0)
  const [endStats, setEndStats] = useState(null)

  const activeHr = demo?.active ? demo.hr : polar?.hr
  const connected = demo?.active || ['connected', 'calibrating', 'reconnecting'].includes(polar?.status)

  const patterns = {
    coherence: {
      name: 'Coherencia Cardíaca',
      badge: '5s Inhala · 5s Exhala',
      desc: 'Ritmo óptimo (~6 resp/min) para equilibrar el sistema autónomo y maximizar HRV.',
      steps: [
        { phase: 'inhale', duration: 5, label: 'Inhalá suavemente' },
        { phase: 'exhale', duration: 5, label: 'Exhalá despacio' }
      ]
    },
    relax: {
      name: 'Relajación (4-7-8)',
      badge: '4s Inhala · 7s Retén · 8s Exhala',
      desc: 'Calma el sistema simpático de alerta, reduce la ansiedad e induce el sueño.',
      steps: [
        { phase: 'inhale', duration: 4, label: 'Inhalá por la nariz' },
        { phase: 'hold', duration: 7, label: 'Retené el aire' },
        { phase: 'exhale', duration: 8, label: 'Exhalá por la boca' }
      ]
    },
    box: {
      name: 'Caja (Box 4-4-4-4)',
      badge: '4s Inhala · 4s Retén · 4s Exhala · 4s Retén',
      desc: 'Técnica de alta concentración y control cardiovascular bajo exigencia.',
      steps: [
        { phase: 'inhale', duration: 4, label: 'Inhalá' },
        { phase: 'hold', duration: 4, label: 'Retené con aire' },
        { phase: 'exhale', duration: 4, label: 'Exhalá' },
        { phase: 'hold2', duration: 4, label: 'Retené vacío' }
      ]
    }
  }

  const steps = patterns[pattern].steps

  // Start Meditation
  const handleStart = () => {
    setStartHr(activeHr || 70)
    setStartRmssd(hrv?.rmssd || 40)
    const allHistory = demo?.active ? demo.history : polar?.hrHistory
    setHistoryAtStartLength(allHistory?.length || 0)
    setMeditationSeconds(0)
    setEndStats(null)
    setIsActive(true)
  }

  // End Meditation
  const handleEnd = () => {
    setIsActive(false)
    const allHistory = demo?.active ? demo.history : polar?.hrHistory
    const sessionReadings = allHistory?.slice(historyAtStartLength) || []
    
    let avgHr = activeHr || 70
    if (sessionReadings.length > 0) {
      const validHrs = sessionReadings.map(r => r.hr).filter(h => h > 30 && h < 220)
      if (validHrs.length > 0) {
        avgHr = Math.round(validHrs.reduce((a, b) => a + b, 0) / validHrs.length)
      }
    }

    const currentRmssd = hrv?.rmssd || 40
    setEndStats({
      duration: meditationSeconds,
      avgHr,
      endRmssd: currentRmssd,
      gain: currentRmssd - (startRmssd || 40)
    })

    if (sessionReadings.length >= 5) {
      onUploadSession?.(sessionReadings, { 
        deviceName: `Polar H10 (Meditación: ${patterns[pattern].name})`, 
        source: 'polar',
        steps: 0 
      })
    }
  }

  // Drive the timers and phase transitions
  useEffect(() => {
    if (!isActive) return

    let currentIdx = 0
    let timeLeft = steps[0].duration
    
    setBreathingPhase(steps[0].phase)
    setBreathingSeconds(timeLeft)
    setStepDuration(timeLeft)

    try {
      if ('vibrate' in navigator) navigator.vibrate([35])
    } catch (e) {}

    const interval = setInterval(() => {
      setMeditationSeconds(sec => sec + 1)
      timeLeft -= 1

      if (timeLeft <= 0) {
        currentIdx = (currentIdx + 1) % steps.length
        const nextStep = steps[currentIdx]
        timeLeft = nextStep.duration
        setBreathingPhase(nextStep.phase)
        setBreathingSeconds(timeLeft)
        setStepDuration(nextStep.duration)

        try {
          if ('vibrate' in navigator) {
            if (nextStep.phase === 'inhale') navigator.vibrate([40])
            else if (nextStep.phase === 'hold' || nextStep.phase === 'hold2') navigator.vibrate([20, 30, 20])
            else navigator.vibrate([50])
          }
        } catch (e) {}
      } else {
        setBreathingSeconds(timeLeft)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [isActive, pattern])

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  const getPhaseColor = () => {
    switch (breathingPhase) {
      case 'inhale': return '#34c759' // Green
      case 'hold':
      case 'hold2':  return '#af52de' // Purple
      case 'exhale': return '#007aff' // Blue
      default:       return '#34c759'
    }
  }

  const activeColor = getPhaseColor()

  // Dynamic Orb scaling
  const getOrbStyle = () => {
    let scale = 1
    if (breathingPhase === 'inhale') {
      const progress = (stepDuration - breathingSeconds) / stepDuration
      scale = 1 + progress * 0.35
    } else if (breathingPhase === 'exhale') {
      const progress = (stepDuration - breathingSeconds) / stepDuration
      scale = 1.35 - progress * 0.35
    } else {
      scale = 1.35
    }

    return {
      width: 'clamp(140px, 28vh, 190px)',
      height: 'clamp(140px, 28vh, 190px)',
      borderRadius: '50%',
      background: `radial-gradient(circle, ${activeColor}33 0%, ${activeColor}08 70%)`,
      border: `2px solid ${activeColor}`,
      boxShadow: `0 0 40px ${activeColor}40`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transform: `scale(${scale})`,
      transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.6s ease, box-shadow 0.6s ease',
      zIndex: 1,
      position: 'relative'
    }
  }

  // Contenedor Viewport-Fit: NUNCA scrollea, se adapta 100% al alto de pantalla disponible
  const containerStyle = {
    position: isFullscreen ? 'fixed' : 'relative',
    inset: isFullscreen ? 0 : 'auto',
    width: '100%',
    maxWidth: isFullscreen ? '100vw' : 680,
    height: isFullscreen ? '100dvh' : 'calc(100dvh - 140px)',
    maxHeight: isFullscreen ? '100dvh' : 660,
    minHeight: isFullscreen ? '100dvh' : 430,
    margin: isFullscreen ? 0 : '0 auto',
    background: isFullscreen 
      ? (isDarkTheme ? '#08080a' : '#f9fafb') 
      : 'var(--card-bg)',
    borderRadius: isFullscreen ? 0 : 20,
    border: isFullscreen ? 'none' : '1px solid var(--border)',
    padding: isFullscreen ? 'max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))' : '16px 18px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'hidden',
    zIndex: isFullscreen ? 99999 : 1,
    animation: 'fadeIn 0.25s ease-out'
  }

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes meditationHeartbeat {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.16); }
          50% { transform: scale(1); }
        }
        .meditation-heart-pulse {
          animation: meditationHeartbeat 1.2s infinite ease-in-out;
        }
      `}</style>

      {/* ── 1. SELECTOR PREVIO (SIN SCROLL) ── */}
      {!isActive && !endStats && (
        <>
          {/* Header Superior Compacto */}
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <IconMeditation size={24} color="#8b5cf6" />
              <h2 style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)', margin: 0, letterSpacing: -0.4 }}>
                Coherencia Cardíaca y Respiración
              </h2>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-soft)', margin: '0 auto 8px', maxWidth: 440, lineHeight: 1.4 }}>
              Sincronizá tu respiración a 5.5 rpm con tus latidos para reducir el estrés y elevar tu HRV.
            </p>

            {/* Chip de sensor */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: connected ? 'rgba(52,199,89,0.12)' : 'rgba(255,159,10,0.12)', padding: '3px 10px', borderRadius: 14 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: connected ? '#34c759' : '#ff9f0a' }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: connected ? '#34c759' : '#ff9f0a' }}>
                {connected ? `Sensor Polar · ${activeHr || '--'} BPM` : 'Sensor no conectado'}
              </span>
            </div>
          </div>

          {/* Selector de programas horizontal y compacto */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 540, margin: '8px 0' }}>
            {Object.entries(patterns).map(([key, p]) => {
              const selected = pattern === key
              return (
                <div
                  key={key}
                  onClick={() => setPattern(key)}
                  style={{
                    background: selected ? 'var(--field-bg)' : 'transparent',
                    border: '1.5px solid ' + (selected ? '#8b5cf6' : 'var(--border)'),
                    borderRadius: 14,
                    padding: '10px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>{p.name}</span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, background: selected ? 'rgba(139,92,246,0.15)' : 'var(--chip-bg)', color: selected ? '#8b5cf6' : 'var(--text-faint)', padding: '2px 7px', borderRadius: 6 }}>
                        {p.badge}
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 0', lineHeight: 1.3 }}>{p.desc}</p>
                  </div>
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                    border: '1.5px solid ' + (selected ? '#8b5cf6' : 'var(--border)'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {selected && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6' }} />}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Botón Comenzar */}
          <div style={{ width: '100%', maxWidth: 540 }}>
            <button
              onClick={handleStart}
              style={{
                width: '100%',
                background: '#8b5cf6',
                color: '#fff',
                border: 'none',
                borderRadius: 14,
                padding: '12px',
                fontSize: 14.5,
                fontWeight: 800,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: '0 6px 20px rgba(139,92,246,0.35)',
                transition: 'transform 0.15s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              ▶ Comenzar Sesión
            </button>
          </div>
        </>
      )}

      {/* ── 2. SESIÓN ACTIVA (EXPEDICIÓN ZEN COMPACTA) ── */}
      {isActive && (
        <>
          {/* Barra Superior */}
          <div style={{ zIndex: 1, display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {patterns[pattern].name}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                style={{
                  background: 'var(--chip-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '4px 10px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: activeColor,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                {isFullscreen ? '✕ Salir Zen' : '⛶ Zen'}
              </button>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: '#ff3b30', fontVariantNumeric: 'tabular-nums', background: 'rgba(255,59,48,0.1)', padding: '3px 8px', borderRadius: 8 }}>
                {formatTimer(meditationSeconds)}
              </span>
            </div>
          </div>

          {/* Orbe Central de Respiración */}
          <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%', margin: '4px 0' }}>
            <div style={getOrbStyle()}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <span style={{ fontSize: 'clamp(42px, 8vh, 60px)', fontWeight: 900, color: activeColor, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {breathingSeconds}
                </span>
                <div className="meditation-heart-pulse" style={{ display: 'flex', alignItems: 'center', gap: 4, background: isDarkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', padding: '2px 8px', borderRadius: 10, marginTop: 2 }}>
                  <IconHeart size={10} color={activeColor} fill={activeColor} />
                  <span style={{ fontSize: 11, fontWeight: 800, color: activeColor, fontVariantNumeric: 'tabular-nums' }}>
                    {activeHr || '--'}
                  </span>
                  <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-faint)' }}>BPM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Guía de Fase y Biofeedback en Tiempo Real */}
          <div style={{ zIndex: 1, textAlign: 'center', width: '100%', marginBottom: 6 }}>
            <h1 style={{
              fontSize: 'clamp(24px, 4.5vh, 34px)', fontWeight: 900, letterSpacing: 1.5, margin: '0 0 2px',
              color: activeColor, textTransform: 'uppercase', transition: 'color 0.4s ease'
            }}>
              {breathingPhase === 'inhale' ? 'Inhalá' : breathingPhase === 'exhale' ? 'Exhalá' : 'Retené'}
            </h1>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-soft)', margin: '0 0 10px' }}>
              {steps.find(s => s.phase === breathingPhase)?.label || ''}
            </p>

            {/* Fila compacta de Biofeedback */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, background: 'var(--inset-bg)', padding: '5px 14px', borderRadius: 12 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-faint)' }}>
                Frecuencia: <strong style={{ color: '#007aff', fontWeight: 800 }}>{hrv?.respRate ? `${hrv.respRate} RPM` : '5.5 RPM'}</strong>
              </span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-faint)' }}>
                HRV en vivo: <strong style={{ color: '#34c759', fontWeight: 800 }}>{hrv?.rmssd ? `${hrv.rmssd} ms` : '--'}</strong>
              </span>
            </div>
          </div>

          {/* Botón Finalizar */}
          <div style={{ zIndex: 1, width: '100%', maxWidth: 440 }}>
            <button
              onClick={handleEnd}
              style={{
                width: '100%',
                background: 'var(--chip-bg)',
                color: 'var(--text-soft)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: '9px',
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)' }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-soft)' }}
            >
              Finalizar Sesión
            </button>
          </div>
        </>
      )}

      {/* ── 3. RESUMEN COMPACTO (SIN SCROLL) ── */}
      {endStats && (
        <div style={{ width: '100%', maxWidth: 500, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', padding: '6px 0' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <IconMeditation size={32} color="#34c759" />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)', margin: '0 0 4px' }}>
              ¡Sesión Completada!
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-soft)', margin: '0 0 14px' }}>
              Tu sistema nervioso autónomo entró en sintonía de recuperación.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              <div style={{ background: 'var(--chip-bg)', borderRadius: 12, padding: '10px' }}>
                <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 2px' }}>Duración</p>
                <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{formatTimer(endStats.duration)}</p>
              </div>
              <div style={{ background: 'var(--chip-bg)', borderRadius: 12, padding: '10px' }}>
                <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 2px' }}>Pulso promedio</p>
                <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{endStats.avgHr} BPM</p>
              </div>
              <div style={{ background: 'var(--chip-bg)', borderRadius: 12, padding: '10px' }}>
                <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 2px' }}>HRV Inicial</p>
                <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-muted)', margin: 0 }}>{startRmssd ? `${startRmssd} ms` : '--'}</p>
              </div>
              <div style={{ background: 'var(--chip-bg)', borderRadius: 12, padding: '10px' }}>
                <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 2px' }}>HRV Final</p>
                <p style={{ fontSize: 16, fontWeight: 800, color: '#34c759', margin: 0 }}>{endStats.endRmssd ? `${endStats.endRmssd} ms` : '--'}</p>
              </div>
            </div>

            <div style={{
              background: endStats.gain > 0 ? 'rgba(52,199,89,0.1)' : 'var(--chip-bg)',
              border: `1px solid ${endStats.gain > 0 ? 'rgba(52,199,89,0.3)' : 'var(--border)'}`,
              borderRadius: 12, padding: '10px 12px', color: endStats.gain > 0 ? '#34c759' : 'var(--text-soft)',
              fontSize: 12, lineHeight: 1.4, textAlign: 'left'
            }}>
              {endStats.gain > 0 ? (
                <span><strong>¡Variabilidad cardíaca aumentada en +{endStats.gain} ms!</strong> Indica una activación parasimpática y relajación profunda.</span>
              ) : (
                <span><strong>✓ Sesión registrada.</strong> La respiración rítmica entrena la resiliencia cardiovascular.</span>
              )}
            </div>
          </div>

          <button
            onClick={() => { setEndStats(null); setIsFullscreen(false); }}
            style={{
              width: '100%',
              background: 'var(--btn-strong)',
              color: 'var(--seg-active-text-inv)',
              border: 'none',
              borderRadius: 12,
              padding: '11px',
              fontSize: 13.5,
              fontWeight: 800,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginTop: 10
            }}
          >
            Listo, volver a empezar
          </button>
        </div>
      )}
    </div>
  )
}
